<template>
  <div class="room-filter">
    <modal
      variant="drawer"
      custom-footer
      @input="handleOpenModal"
      @close="handleCloseModal"
    >
      <template v-slot:default="{ activate, isActive }">
        <slot v-bind="{ activate, isActive, resetAllFilters, totalFiltersSelected }" />
      </template>

      <template v-slot:header>
        <div class="room-filter--modal-title">
          <span>{{ tc("booking.filters") }}</span>
        </div>
      </template>

      <template v-slot:body>
        <div class="form">
          <div v-if="showAccessibleFilter" class="room-filter--accessible-room">
            <label>
              <input
                data-field-type="CheckBoxSet"
                id="accessible_rooms"
                name="accessible_rooms"
                type="checkbox"
                :aria-label="tc('booking.showAccessibleRooms')"
                v-model="isAccessibleRoom"
              />
              {{ tc("booking.showAccessibleRooms") }}
              <span class="icon-wheelchair-black"></span>
            </label>
          </div>

          <div class="accordion component">
            <ul
              class="accordion-wrapper container"
              aria-label="Accordion Control Group Buttons"
            >
              <accordion-tab v-if="filteredBedTypeFacets.length > 0" start-expanded>
                <li slot-scope="{ toggleTab, expanded }" class="accordion-item">
                  <button
                    id="accordion-control-id"
                    class="accordion-item-label room-filter--filter-group"
                    v-bind:class="expanded ? 'expanded' : ''"
                    aria-controls="content-id"
                    v-bind:aria-expanded="expanded ? 'true' : 'false'"
                    v-on:click.prevent="toggleTab"
                    v-on:enter="toggleTab"
                    v-on:keydown.space.prevent="toggleTab"
                  >
                    <div>
                      <span class="room-filter--filter-group-title">{{ tc("booking.bedType") }}</span>
                      <button
                        v-if="selectedBedTypes.length > 0"
                        class="room-filter--filter-group-reset"
                        @click.stop="selectedBedTypes = []"
                      >
                        {{ tc("booking.reset") }}
                      </button>
                    </div>
                    <span
                      v-if="selectedBedTypes.length > 0"
                      class="room-filter--filter-group-status"
                      >{{ selectedBedTypes.length }}</span
                    >
                  </button>

                  <div
                    id="content-id"
                    class="accordion-item-content"
                    :aria-hidden="expanded ? 'false' : 'true'"
                  >
                    <div class="accordion-item-content-wrapper">
                      <ul class="room-filter--filter-group-bed-types">
                        <li
                          v-for="bedType in filteredBedTypeFacets"
                          :key="bedType.id"
                          class="input checkbox"
                          :class="{ 'bed-type-hidden': bedTypeIsHidden(bedType.id) }"
                        >
                          <label
                            :for="bedType.id"
                            :class="{ disabled: bedTypeIsDisabled(bedType.id) }"
                          >
                            <input
                              type="checkbox"
                              data-field-type="CheckBoxSet"
                              :tabindex="expanded ? '0' : '-1'"
                              :id="bedType.id"
                              :value="bedType.id"
                              v-model="selectedBedTypes"
                              :disabled="bedTypeIsDisabled(bedType.id)"
                            />
                            {{ bedType.title }}
                          </label>
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </accordion-tab>
              <accordion-tab v-if="filteredHighlights.length > 0" :start-expanded="expandHighlightSection">
                <li slot-scope="{ toggleTab, expanded }" class="accordion-item">
                  <button
                    id="accordion-control-id"
                    class="accordion-item-label room-filter--filter-group"
                    v-bind:class="expanded ? 'expanded' : ''"
                    aria-controls="content-id"
                    v-bind:aria-expanded="expanded ? 'true' : 'false'"
                    v-on:click.prevent="toggleTab"
                    v-on:enter="toggleTab"
                    v-on:keydown.space.prevent="toggleTab"
                  >
                    <div>
                      <span class="room-filter--filter-group-title">{{ tc("booking.highlights") }}</span>
                      <button
                        v-if="selectedHighlights.length > 0"
                        class="room-filter--filter-group-reset"
                        @click.stop="selectedHighlights = []"
                      >
                      {{ tc("booking.reset") }}
                      </button>
                    </div>
                    <span
                      v-if="selectedHighlights.length > 0"
                      class="room-filter--filter-group-status"
                      >{{ selectedHighlights.length }}</span
                    >
                  </button>

                  <div
                    id="content-id"
                    class="accordion-item-content"
                    :aria-hidden="expanded ? 'false' : 'true'"
                  >
                    <div class="accordion-item-content-wrapper">
                      <ul class="room-filter--filter-group-highlights">
                        <li
                          v-for="highlight in filteredHighlights"
                          :key="highlight.id"
                          class="input checkbox"
                        >
                          <label
                            :for="highlight.id"
                            :class="{ disabled: highlightIsDisabled(highlight.id) }"
                          >
                            <input
                              type="checkbox"
                              data-field-type="CheckBoxSet"
                              :tabindex="expanded ? '0' : '-1'"
                              :id="highlight.id"
                              :value="highlight.id"
                              :disabled="highlightIsDisabled(highlight.id)"
                              v-model="selectedHighlights"
                            />
                            {{ highlight.title }}
                          </label>
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </accordion-tab>
            </ul>
          </div>
        </div>
      </template>

      <template v-slot:footer="{ deactivate }">
        <div class="room-filter--footer">
          <div class="room-filter--footer-results">
            <div class="room-filter--footer-results-text">
              {{ filteredRooms.length }} {{ tc("booking.matchingResults") }}
            </div>
            <button
              v-if="hasFilterSelected"
              class="room-filter--footer-results-reset"
              @click="resetAllFilters"
            >
            {{ tc("booking.resetAll") }}
            </button>
          </div>
          <div class="room-filter--footer-cta">
            <button @click="deactivate">{{ tc("booking.apply") }}</button>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapMutations } from "vuex";
  import sift from 'sift'
  export default {
    name: "room-filter",
    components: {},
    props: {
      hasAccessibleRooms: {
        type: Boolean,
      },
      accessibleSelected: {
        type: Boolean,
      },
      roomTypeFilter: {
        type: Object,
      },
      rooms: {
        type: Array,
        default: [],
      },
    },
    data() {
      return {
        isAccessibleRoom: false,
        availableBedTypesFacets: [],
        availableHighlightsFacets: [],
        filteredRooms: [],
        roomsByTypeFilter: [],
        isOpen: false
      };
    },
    mounted() {
      if(this.accessibleSelected) {
        this.isAccessibleRoom = true
      }
      this.selectedBedTypes = this.selectedBedTypesFacets
      this.selectedHighlights = this.selectedHighlightsFacets
      this.filterRooms()
    },
    watch: {
      roomTypeFilter() {
        this.filterRooms()
      } ,
      rooms(val) {
        this.filteredRooms = val
      },
      accessibleSelected(val) {
        this.isAccessibleRoom = val;
      },
      isAccessibleRoom(val) {
        if (val) {
          const accessibilityCta = document.querySelector(
            "#room-filter-accessible"
          );
          accessibilityCta.click();
        } else if(this.roomTypeFilter?.roomType) {
          this.$nextTick(() => this.filterRooms())
        } else {
          const allCta = document.querySelector("#room-filter-all");
          allCta.click();
        }
      },
      selectedBedTypes() {
        this.filterRooms();
      },
      selectedHighlights() {
        this.filterRooms();
      },
    },
    computed: {
      ...mapState("responsive", { wWidth: "width", wHeight: "height" }),
      ...mapGetters("rooms", ["bedTypeFacets", "highlightsFacets", "selectedBedTypesFacets", "selectedHighlightsFacets"]),
      ...mapGetters("i18n", ["tc"]),
      bindings() {
        return {
          totalFiltersSelected: this.totalFiltersSelected,
          resetAllFilters: this.resetAllFilters,
        };
      },
      filteredBedTypeFacets() {
        return this.bedTypeFacets.filter((bed) => !this.bedTypeIsHidden(bed.id))
      },
      filteredHighlights() {
        return this.highlightsFacets.filter((h) => !this.highlightIsHidden(h.id))
      },
      selectedBedTypes: {
        get() {
          return this.selectedBedTypesFacets;
        },
        set(value) {
          this.UPDATE_SELECTED_BED_TYPES(value);
        },
      },
      selectedHighlights: {
        get() {
          return this.selectedHighlightsFacets;
        },
        set(value) {
          this.UPDATE_SELECTED_HIGHLIGHTS(value);
        },
      },
      totalFiltersSelected() {
        let total = 0;
        total =
          this.selectedBedTypes.length +
          this.selectedHighlights.length +
          (this.isAccessibleRoom ? 1 : 0);
        return total;
      },
      hasFilterSelected() {
        return this.isAccessibleRoom || this.selectedBedTypes.length > 0 || this.selectedHighlights.length > 0
      },
      hasRoomTypeFilterSelected() {
        return !!Object.keys(this.roomTypeFilter).length
      },
      filteredRoomsHasAccessibleRoom () {
        return !!this.filteredRooms
        .map((r) => !!r.bedType.find((b) => b.accessible))
        .find((v) => !!v);
      },
      showAccessibleFilter() {
        return this.hasAccessibleRooms && this.filteredRoomsHasAccessibleRoom
      },
      expandHighlightSection() {
        return this.wWidth >= 768 && this.wHeight > 812
      }
    },
    methods: {
      ...mapMutations("rooms", ["UPDATE_SELECTED_HIGHLIGHTS", "UPDATE_SELECTED_BED_TYPES"]),
      resetAllFilters() {
        this.isAccessibleRoom = false;
        this.selectedBedTypes = [];
        this.selectedHighlights = [];
      },

      handleCloseModal() {
        document.body.classList.remove("no-scroll");
        this.isOpen = false
      },

      handleOpenModal() {
        document.body.classList.add("no-scroll");
        this.isOpen = true
      },
      getAvailableBedTypesFacets(rooms) {
        const bedTypesSet = new Set();
        rooms.forEach((room) => {
          room.bedType.forEach((bed) => {
              bedTypesSet.add(bed.bedTypeFacet);
          });
        });
        return [...bedTypesSet]
      },
      getAvailableHighlightsFacets(rooms) {
        const highlightsSet = new Set();
        rooms.forEach((room) => {
          room.highlights.forEach((highlight) => {
            highlightsSet.add(highlight.id);
          });
        });
        return [...highlightsSet]
      },
      updateAvailableFilters(rooms) {
        this.availableBedTypesFacets = this.getAvailableBedTypesFacets(rooms);
        this.availableHighlightsFacets = this.getAvailableHighlightsFacets(rooms);
      },

      bedTypeIsHidden(bedTypeId) {
        const availableBedTypeFromAllRooms = this.getAvailableBedTypesFacets(this.roomsByTypeFilter)
        const bedTypeMatchInAllRooms = availableBedTypeFromAllRooms.some((type) =>
          type.includes(bedTypeId)
        )

        return !bedTypeMatchInAllRooms
      },

      highlightIsHidden(highlightId) {
        const availableHighlightFromAllRooms = this.getAvailableHighlightsFacets(this.roomsByTypeFilter)
        const highlightMatchInAllRooms = availableHighlightFromAllRooms.some((type) =>
          type.includes(highlightId)
        );

        return !highlightMatchInAllRooms
      },

      bedTypeIsDisabled(bedTypeId) {
        return !this.availableBedTypesFacets.some((type) =>
          type.includes(bedTypeId)
        );
      },

      highlightIsDisabled(highlightId) {
        return !this.availableHighlightsFacets.some((item) =>
          item.includes(highlightId)
        );
      },
      filteredRoomsByFacets(rooms, checkAvailability = true) {
        return rooms.filter((room) => {
          // Checking room accessibility
          if (checkAvailability && this.isAccessibleRoom && !room.bedType.some((bed) => bed.accessible === true)) {
            return false;
          }
          // Checking for the number of selected bed types.
          if (this.selectedBedTypes.length > 0) {
            const bedTypeIds = room.bedType.map((bed) => bed.bedTypeFacet);
            const isBedTypeMatch = this.selectedBedTypes.some((selectedType) => bedTypeIds.some(bedType => bedType.includes(selectedType)));


            if (!isBedTypeMatch) {
              return false;
            }
          }
          // Check for selected highlights
          if (this.selectedHighlights.length > 0) {
            const highlightIds = room.highlights.map((highlight) => highlight.id);
            const isHighlightMatch = this.selectedHighlights.every((selectedHighlight) => highlightIds.includes(selectedHighlight));

            if (!isHighlightMatch) {
              return false;
            }
          }
          // If a room has passed all the filters, it remains in the result.
          return true;
        });
      },
      filterRooms() {
        let roomsToFilter = this.hasRoomTypeFilterSelected ? this.rooms.filter(sift(this.roomTypeFilter)) : this.rooms;

        this.roomsByTypeFilter = roomsToFilter;
        this.filteredRooms = this.filteredRoomsByFacets(roomsToFilter);
        this.updateAvailableFilters(this.filteredRooms);
        this.$emit('updateFilteredRooms', this.filteredRooms);
      },
    },
  };
</script>
